import EmojiPicker from "emoji-picker-react";
import {
  Calendar as CalendarIcon,
  Download,
  Image,
  Menu,
  Mic,
  Paperclip,
  Pause,
  Play,
  Send,
  Smile,
  Video,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { io, Socket } from "socket.io-client";
import { getToken, getUserInfo } from "../../services/auth.service";
import {
  CommunityMessage,
  getCommunityMessages,
  postCommunityMessage,
} from "../../services/community.service";
import { getUserProfileById } from "../../services/expositor.service";

export default function Community() {
  const [messages, setMessages] = useState<CommunityMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const MAX_UPLOAD_SIZE = 8 * 1024 * 1024;
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<any>(null);
  const socketRef = useRef<Socket | null>(null);
  const me = getUserInfo();
  const [profiles, setProfiles] = useState<Record<number, any>>({});
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  const normalizeMessage = (m: CommunityMessage): CommunityMessage => {
    const uid = (m as any).user_id ?? (me && me.id) ?? 0;
    const uname = m.user_name ?? (me && me.name) ?? "";
    const ca = ((): string => {
      const v: any = (m as any).created_at;
      if (!v) return new Date().toISOString();
      if (typeof v === "string") return v;
      try {
        return new Date(v).toISOString();
      } catch {
        return new Date().toISOString();
      }
    })();
    return { ...m, user_id: uid as number, user_name: uname, created_at: ca };
  };

  const loadProfilesFor = async (ids: number[]) => {
    const unique = Array.from(new Set(ids)).filter(
      (id) => profiles[id] === undefined
    );
    if (unique.length === 0) return;
    const fetched = await Promise.all(
      unique.map(async (id) => {
        try {
          const res = await getUserProfileById(id);
          return { id, profile: res?.user };
        } catch {
          return { id, profile: null };
        }
      })
    );
    const dict: Record<number, any> = {};
    fetched.forEach(({ id, profile }) => {
      if (profile) dict[id] = profile;
    });
    if (Object.keys(dict).length > 0) setProfiles((p) => ({ ...p, ...dict }));
  };

  const load = () => {
    getCommunityMessages().then((m) => {
      const mm = m.map(normalizeMessage);
      setMessages(mm);
      loadProfilesFor(mm.map((x) => x.user_id));
    });
  };

  const [filterDate, setFilterDate] = useState<string>("");
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const lastMessageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dateKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  const isToday = (d: Date) => {
    const t = new Date();
    return (
      d.getFullYear() === t.getFullYear() &&
      d.getMonth() === t.getMonth() &&
      d.getDate() === t.getDate()
    );
  };
  const isYesterday = (d: Date) => {
    const t = new Date();
    const y = new Date(t);
    y.setDate(t.getDate() - 1);
    return (
      d.getFullYear() === y.getFullYear() &&
      d.getMonth() === y.getMonth() &&
      d.getDate() === y.getDate()
    );
  };
  const labelForDate = (d: Date) => {
    if (isToday(d)) return "Hoje";
    if (isYesterday(d)) return "Ontem";
    return d.toLocaleDateString("pt-BR");
  };
  const shownMessages = messages.slice();
  shownMessages.sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const lastIndexByDate: Record<string, number> = {};
  shownMessages.forEach((m, idx) => {
    lastIndexByDate[dateKey(new Date(m.created_at))] = idx;
  });

  useEffect(() => {
    if (filterDate) {
      const el = lastMessageRefs.current[filterDate];
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [filterDate, shownMessages]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!showDatePicker) return;
      const t = e.target as Node;
      if (datePickerRef.current && !datePickerRef.current.contains(t)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showDatePicker]);

  useEffect(() => {
    load();
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");
    const s = io(base, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: { token: getToken() },
    });
    socketRef.current = s;
    s.on("community:new", (msg: CommunityMessage) => {
      const nm = normalizeMessage(msg);
      setMessages((prev) =>
        prev.some((m) => m.id === nm.id) ? prev : [...prev, nm]
      );
      loadProfilesFor([nm.user_id]);
      scrollToBottom();
    });
    s.on("community:delete", ({ id }: { id: number }) => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
    });
    return () => {
      s.disconnect();
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    if (!filterDate) scrollToBottom();
  }, [messages, filterDate]);

  const handleSendMessage = async () => {
    if (!inputText.trim() && !selectedFile && !audioBlob) return;
    if (isSending) return;
    setIsSending(true);
    try {
      let toSendFile: File | undefined = undefined;
      if (selectedFile) toSendFile = selectedFile;
      if (!toSendFile && audioBlob) {
        const type = audioBlob.type || "audio/webm";
        const ext = type.startsWith("audio/mp4")
          ? "m4a"
          : type.startsWith("audio/mpeg")
          ? "mp3"
          : type.startsWith("audio/ogg")
          ? "ogg"
          : "webm";
        toSendFile = new File([audioBlob], `audio.${ext}`, { type });
      }
      if (toSendFile) {
        const isDoc =
          toSendFile.type.startsWith("application/") ||
          toSendFile.type.startsWith("text/");
        if (
          (toSendFile.type.startsWith("image/") ||
            toSendFile.type.startsWith("video/") ||
            isDoc) &&
          toSendFile.size > MAX_UPLOAD_SIZE
        ) {
          alert("O arquivo excede o limite de 8MB.");
          setIsSending(false);
          return;
        }
      }
      const saved = await postCommunityMessage(inputText, toSendFile);
      const nm = normalizeMessage(saved);
      setMessages((prev) =>
        prev.some((m) => m.id === nm.id) ? prev : [...prev, nm]
      );
      setInputText("");
      setSelectedFile(null);
      setPreviewUrl(null);
      setAudioBlob(null);
      scrollToBottom();
    } catch (e) {
      alert("Falha ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSending(false);
    }
  };

  const wrapAt = (text: string, width = 56) => {
    const lines = text.split("\n");
    const wrapped: string[] = [];
    for (const line of lines) {
      let l = line;
      while (l.length > width) {
        const slice = l.slice(0, width);
        let idx = slice.lastIndexOf(" ");
        if (idx <= 0) idx = width;
        wrapped.push(l.slice(0, idx).trimEnd());
        l = l.slice(idx).replace(/^\s+/, "");
      }
      wrapped.push(l);
    }
    return wrapped.join("\n");
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true },
      });
      const getSupportedAudioMime = () => {
        const cands = ["audio/mp4", "audio/mpeg", "audio/webm", "audio/ogg"];
        return typeof (window as any).MediaRecorder !== "undefined" &&
          (window as any).MediaRecorder.isTypeSupported
          ? cands.find((t) =>
              (window as any).MediaRecorder.isTypeSupported(t)
            ) || ""
          : "";
      };
      const mime = getSupportedAudioMime();
      mediaRecorderRef.current = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      const audioChunks: BlobPart[] = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && (event.data as any).size > 0)
          audioChunks.push(event.data);
      };
      mediaRecorderRef.current.onstop = () => {
        const chunkType = (audioChunks[0] as any)?.type || "";
        const finalType = mime || chunkType || "audio/webm";
        const blob = new Blob(audioChunks, { type: finalType });
        setAudioBlob(blob);
        try {
          setPreviewUrl(URL.createObjectURL(blob));
        } catch {}
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch {
      alert("Não foi possível acessar o microfone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const handleSelectFile = (f: File | null) => {
    if (!f) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    const isDoc =
      f.type.startsWith("application/") || f.type.startsWith("text/");
    if (
      (f.type.startsWith("image/") || f.type.startsWith("video/") || isDoc) &&
      f.size > MAX_UPLOAD_SIZE
    ) {
      alert("O arquivo excede o limite de 8MB.");
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    setSelectedFile(f);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getInstagram = (userId: number) => {
    const profile = profiles[userId];
    const raw = (profile?.instagram_url || "").toString().trim();
    if (!raw) return null;
    const href = raw.startsWith("http")
      ? raw
      : `https://www.instagram.com/${raw.replace(/^@/, "")}/`;
    const handle = raw
      .replace(/^https?:\/\/www\.instagram\.com\//, "")
      .replace(/\/$/, "");
    return { text: raw, href, handle };
  };

  return (
    <div className="flex flex-col lg:flex-row-reverse max-h-[calc(100vh-6rem)] bg-white p-0 gap-8">
      <div className="px-0 mb-3 w-full lg:w-[30%]">
        <div className="bg-white rounded-lg border shadow p-4">
          <div className="grid grid-cols-1 gap-3">
            <div className="rounded-xl">
              <p className="text-sm text-slate-600 leading-relaxed">
                Bem-vindos à Comunidade Praceria. Este espaço existe para
                compartilhar novidades, experiências de empreendedorismo, tirar
                dúvidas, organizar-se e fortalecer a rede do evento. Participe
                com respeito e colaboração para que todos se beneficiem.
              </p>
            </div>
            <div className="rounded-xl">
              <h2 className="text-sm font-semibold tracking-wide text-[#24324D] mb-1">
                O que não pode
              </h2>
              <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                <li>Assuntos fora da Praceria, política ou temas alheios.</li>
                <li>Spam ou autopromoção sem relação direta com o evento.</li>
                <li>Ofensas, discriminação ou linguagem inadequada.</li>
                <li>Links não oficiais ou informações não verificadas.</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Fale apenas sobre temas relacionados à Praceria.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full h-[calc(100vh-8rem)] overflow-hidden">
        <div className="bg-gradient-to-b from-[#24324D] to-[#1a2538] text-white py-4 px-0 shadow rounded-lg flex flex-row justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-wide px-4">
              Comunidade Praceria
            </h1>
            <p className="text-sm text-blue-100 px-4">
              {shownMessages.length} mensagens
            </p>
          </div>
          <div className="px-4 mt-2 flex items-center gap-2 relative">
            <button
              onClick={() => setShowDatePicker((v) => !v)}
              className="p-2 rounded-full border border-white/40 text-white hover:bg-white/10 transition"
              aria-label="Filtrar por data"
              title="Filtrar por data"
            >
              <CalendarIcon size={18} />
            </button>
            {showDatePicker && (
              <div
                ref={datePickerRef}
                className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow z-50 p-2 text-[#24324D] w-[92vw] max-w-[20rem] sm:w-auto sm:max-w-none overflow-auto max-h-[70vh]"
              >
                <Calendar
                  className="w-full"
                  onChange={(value: any) => {
                    const d: Date = Array.isArray(value) ? value[0] : value;
                    setFilterDate(
                      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                        2,
                        "0"
                      )}-${String(d.getDate()).padStart(2, "0")}`
                    );
                    setShowDatePicker(false);
                  }}
                  value={filterDate ? new Date(filterDate) : new Date()}
                />
              </div>
            )}
            {filterDate && (
              <button
                onClick={() => setFilterDate("")}
                className="text-white/90 border border-white/50 rounded px-2 py-1 text-sm"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
        <div
          className={`flex-1 overflow-y-auto py-4 px-0 space-y-4 ${
            selectedFile || audioBlob ? "pb-40" : "pb-24"
          } sm:pb-0`}
        >
          {shownMessages.map((m, idx) => {
            const isMine = me?.id === m.user_id;
            const avatar = (m.user_name || `U${m.user_id}`)
              .split(" ")
              .map((x) => x[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            const timestamp = new Date(m.created_at).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            const insta = getInstagram(m.user_id);
            const d = new Date(m.created_at);
            const prev =
              idx > 0 ? new Date(shownMessages[idx - 1].created_at) : null;
            const showSep = !prev || dateKey(prev) !== dateKey(d);
            return (
              <>
                {showSep && (
                  <div className="flex justify-center">
                    <span className="text-xs bg-slate-200 text-slate-700 px-3 py-1 rounded-full">
                      {labelForDate(d)}
                    </span>
                  </div>
                )}
                <div
                  key={m.id}
                  className={`flex gap-3 ${isMine ? "flex-row-reverse" : ""}`}
                  ref={(el) => {
                    const k = dateKey(new Date(m.created_at));
                    if (lastIndexByDate[k] === idx) {
                      lastMessageRefs.current[k] = el;
                    }
                  }}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
                      isMine
                        ? "bg-gradient-to-b from-[#24324D] to-[#1a2538]"
                        : "bg-sky-600"
                    }`}
                  >
                    {avatar}
                  </div>
                  <div
                    className={`flex flex-col max-w-[85%] sm:max-w-md ${
                      isMine ? "items-end" : ""
                    }`}
                  >
                    <span className="text-xs text-slate-600 mb-1">
                      {m.user_name || `Usuário #${m.user_id}`}
                      {insta && (
                        <>
                          {" - "}
                          <a
                            href={insta.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#24324D] hover:underline"
                          >
                            {insta.handle || insta.text}
                          </a>
                        </>
                      )}
                    </span>
                    <div
                      className={`rounded-2xl px-3 py-2 w-fit max-w-[80vw] sm:max-w-[26rem] md:max-w-[32rem] lg:max-w-[36rem] ${
                        isMine ? "bg-[#24324D] text-white" : "bg-white shadow"
                      }`}
                    >
                      {m.message_type === "image" && (m.url || m.content) && (
                        <img
                          src={m.url || m.content || ""}
                          alt="Imagem"
                          className="rounded-xl max-w-[75vw] sm:max-w-xs w-full mb-2 object-contain"
                        />
                      )}
                      {m.message_type === "video" && (m.url || m.content) && (
                        <video
                          src={m.url || m.content || ""}
                          controls
                          className="rounded-xl max-w-[75vw] sm:max-w-xs w-full mb-2 object-contain"
                        />
                      )}
                      {m.message_type === "audio" && (m.url || m.content) && (
                        <AudioBubble
                          src={m.url || m.content || ""}
                          dark={isMine}
                        />
                      )}
                      {m.message_type === "file" && (m.url || m.content) && (
                        <a
                          href={m.url || m.content || ""}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${
                            isMine ? "text-blue-100" : "text-[#24324D]"
                          } underline`}
                        >
                          Arquivo
                        </a>
                      )}
                      {m.content && m.message_type === "text" && (
                        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                          {m.content}
                        </p>
                      )}
                      <span
                        className={`text-xs mt-1 block ${
                          isMine ? "text-blue-100" : "text-slate-500"
                        }`}
                      >
                        {timestamp}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="bg-white/95 border-t py-3 px-2 sm:px-0 fixed bottom-0 left-0 right-0 z-20 sm:static sm:z-auto pb-[env(safe-area-inset-bottom)]">
          {selectedFile && (
            <div className="mb-2">
              <div className="flex items-center  gap-3 bg-white p-3 rounded-lg border">
                {selectedFile.type.startsWith("image/") && (
                  <img
                    src={previewUrl || ""}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded"
                  />
                )}
                {selectedFile.type.startsWith("video/") && (
                  <video
                    src={previewUrl || ""}
                    className="w-16 h-16 object-cover rounded"
                  />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}
          {audioBlob && !selectedFile && (
            <div className="mb-2">
              <div className="flex flex-row items-stretch gap-3 bg-white p-3 rounded-lg w-full border">
                <div className="flex-1 min-w-0">
                  <AudioBubble src={previewUrl || ""} dark={false} />
                </div>
                <button
                  onClick={() => {
                    setAudioBlob(null);
                    setPreviewUrl(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 sm:self-center"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}
          {isRecording ? (
            <div className="flex items-center gap-3 bg-blue-50 p-4 mx-4 mb-4 rounded-xl">
              <div className="flex-1 flex items-center gap-3">
                <div className="w-3 h-3 bg-[#24324D] rounded-full animate-pulse"></div>
                <span className="text-[#24324D] font-semibold">
                  Gravando... {recordingTime}s
                </span>
              </div>
              <button
                onClick={stopRecording}
                className="px-4 py-2 bg-[#24324D] text-white rounded-lg font-medium hover:opacity-90"
              >
                Parar
              </button>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-end md:items-start  gap-2 px-4 pb-4">
              <div className="hidden sm:flex gap-2">
                <input
                  type="file"
                  ref={imageInputRef}
                  accept="image/*"
                  onChange={(e) =>
                    handleSelectFile(e.target.files?.[0] || null)
                  }
                  className="hidden"
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition"
                >
                  <Image size={22} />
                </button>
                <input
                  type="file"
                  ref={videoInputRef}
                  accept="video/*"
                  onChange={(e) =>
                    handleSelectFile(e.target.files?.[0] || null)
                  }
                  className="hidden"
                />
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition"
                >
                  <Video size={22} />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) =>
                    handleSelectFile(e.target.files?.[0] || null)
                  }
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-full transition"
                >
                  <Paperclip size={22} />
                </button>
              </div>
              <div className="w-full flex gap-2 relative">
                {showMobileActions && (
                  <div className="absolute bottom-full left-0 mb-2 bg-white shadow rounded-xl p-2 flex items-center gap-2 sm:hidden z-30">
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-full"
                    >
                      <Image size={20} />
                    </button>
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-full"
                    >
                      <Video size={20} />
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-full"
                    >
                      <Paperclip size={20} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowMobileActions((v) => !v)}
                  className="p-3 text-slate-600 hover:bg-slate-100 rounded-full transition sm:hidden"
                >
                  <Menu size={20} />
                </button>
                <div className="flex-1 bg-slate-100 rounded-xl flex items-start px-4 py-2.5 relative">
                  <button
                    className="text-slate-600 hover:text-slate-800 mr-2 mt-1"
                    onClick={() => setShowEmoji((s) => !s)}
                  >
                    <Smile size={22} />
                  </button>
                  {showEmoji && (
                    <div className="absolute bottom-full left-0 mb-2 z-10 shadow-lg">
                      <EmojiPicker
                        onEmojiClick={(e) => {
                          setInputText((t) => wrapAt(t + e.emoji));
                          setShowEmoji(false);
                        }}
                        searchDisabled
                        skinTonesDisabled
                        width={280}
                        height={350}
                      />
                    </div>
                  )}
                  <textarea
                    value={inputText}
                    onChange={(e) => {
                      const v = wrapAt(e.target.value);
                      setInputText(v);
                      const el = e.currentTarget;
                      el.style.height = "auto";
                      el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
                    }}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Digite uma mensagem..."
                    rows={1}
                    className="flex-1 bg-transparent outline-none resize-none overflow-y-auto max-h-32 leading-relaxed"
                  />
                </div>
                {inputText.trim() || selectedFile || audioBlob ? (
                  <button
                    onClick={handleSendMessage}
                    disabled={isSending}
                    className="h-11 p-3 ml-1 bg-[#24324D] text-white rounded-full hover:opacity-90 transition"
                  >
                    <Send size={20} />
                  </button>
                ) : (
                  <button
                    onClick={startRecording}
                    className="h-11 p-3 ml-1 bg-[#24324D] text-white rounded-full hover:opacity-90 transition"
                  >
                    <Mic size={20} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function AudioBubble({ src, dark }: { src: string; dark?: boolean }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const color = dark ? "#cfe1ff" : "#24324D";
  const bg = dark ? "bg-blue-100/20" : "bg-slate-100";
  const text = dark ? "text-blue-100" : "text-[#24324D]";
  const btn = dark ? "bg-white/20 text-white" : "bg-[#24324D] text-white";

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  useEffect(() => {
    const a = audioRef.current;
    const c = canvasRef.current;
    if (!a || !c) return;
    let raf = 0;
    let ctx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    const draw = () => {
      if (!analyser) return;
      const canvas = c;
      const g = canvas.getContext("2d");
      if (!g) return;
      const width = canvas.width;
      const height = canvas.height;
      g.clearRect(0, 0, width, height);
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);
      const bars = 32;
      const step = Math.floor(bufferLength / bars);
      const barWidth = width / bars;
      for (let i = 0; i < bars; i++) {
        const v = dataArray[i * step] / 255;
        const h = Math.max(2, v * height);
        g.fillStyle = color;
        g.fillRect(i * barWidth, height - h, barWidth * 0.7, h);
      }
      raf = requestAnimationFrame(draw);
    };
    const onPlay = async () => {
      setPlaying(true);
      if (!ctx) {
        ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const source = ctx.createMediaElementSource(a);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        analyser.connect(ctx.destination);
      }
      draw();
    };
    const onPause = () => {
      setPlaying(false);
      if (raf) cancelAnimationFrame(raf);
    };
    const onTime = () => setCurrent(a.currentTime);
    const onLoaded = () => setDuration(isFinite(a.duration) ? a.duration : 0);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onPause);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      const el = containerRef.current;
      if (!el) return;
      const width = Math.max(160, Math.min(360, Math.floor(el.clientWidth)));
      c.width = width * dpr;
      c.height = 48 * dpr;
      c.style.width = width + "px";
      c.style.height = "48px";
    };
    resize();
    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new ResizeObserver(() => resize());
      if (containerRef.current) ro.observe(containerRef.current);
    }
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onPause);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      if (raf) cancelAnimationFrame(raf);
      if (ctx) ctx.close();
      if (ro && containerRef.current) ro.unobserve(containerRef.current);
    };
  }, [src, dark]);

  const fmt = (s: number) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const ss = Math.floor(s % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${ss}`;
  };

  return (
    <div
      ref={containerRef}
      className={`flex items-center gap-2 ${bg} rounded-xl p-3 w-full`}
    >
      <button
        onClick={toggle}
        className={`rounded-full p-2 ${btn}`}
        aria-label={playing ? "Pausar" : "Tocar"}
      >
        {playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <canvas ref={canvasRef} className="flex-1 min-w-0" />
      <div className={`flex items-center gap-2 ${text}`}>
        <span>{fmt(current)}</span>
        <span>/</span>
        <span>{fmt(duration)}</span>
      </div>
      <a
        href={src}
        download
        className={`ml-auto rounded-full p-2 ${btn}`}
        aria-label="Baixar áudio"
      >
        <Download size={18} />
      </a>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}
